import React, { useEffect, useState, lazy, Suspense } from 'react';
import styled from '@emotion/styled';
import { mediaQuery, Spacings, Fonts } from '@styles/index';
import getTopicGroupAssetObj from '@cont/Syllabus/constants/getTopicGroupAssetObj';
import Tag from 'aquilla/core/atoms/Tag';
import Typography from 'aquilla/core/atoms/Typography';
import Button from 'aquilla/core/molecules/Button';
import Image from '@base/Image';
import EducatorAvatar from '@cont/EducatorProfile/EducatorAvatar';
import Play from 'aquilla/icons/actions/Play';
import { useIsMobile } from '@stores/AppCommonData';
import Dialog from '@comp/Dialog/Dialog';
import EducatorThumbnailBackground from 'aquilla/core/atoms/EducatorThumbnailBackground';
import Forward from 'aquilla/icons/actions/Forward';
import Link from '@base/Link/Link';
import { nFormatter } from '@utils/textHelper';
import PlayContained from 'aquilla/icons/actions/PlayContained';
import segmentStorage from '@events/segmentStorage';
import useLessonDetails from './useLessonDetails';
import { getAuthorName, LANGUAGE_MAP } from './utils';
import { MAIN_CTA, PREVIEW_THUMBNAIL } from './constants/events';

const ShareDialog = lazy(() =>
  import('../../../Composite/ShareDialog/ShareDialog')
);

const HeaderContainer = styled.div`
  display: grid;
  gap: 102px;
  grid-template-columns: repeat(2, 1fr);
  width: ${Spacings.APP_CONTAINER_WIDTH};
  margin: auto;
  margin-top: ${Spacings.SPACING_5B};
  ${mediaQuery.uptoMobile} {
    width: 100%;
    grid-template-columns: 1fr;
    gap: ${Spacings.SPACING_5B};
  }
`;
const PlayListInfo = styled.div`
  display: grid;
  width: 534px;
  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;

const MetaInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacings.SPACING_2B};
`;
const Subject = styled(Typography)`
  font-size: ${Fonts.ULTRA_SMALL};
  line-height: 12px;
  text-transform: uppercase;
`;
const LanguageTag = styled(Tag)``;

const Title = styled(Typography)`
  margin-top: ${Spacings.SPACING_3B};
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.H4};
    line-height: 120%;
    margin-top: ${Spacings.SPACING_2B};
  }
`;

const Description = styled(Typography)`
  margin-top: ${Spacings.SPACING_4B};
  ${mediaQuery.uptoMobile} {
    margin-bottom: ${Spacings.SPACING_0B};
  }
`;

const EducatorInfoWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacings.SPACING_4B};
  margin-top: ${Spacings.SPACING_4B};
`;

const InfoWrapper = styled.div``;

const Name = styled(Typography)`
  grid-row: 1/2;
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.SMALL};
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacings.SPACING_6B};
  margin-top: ${Spacings.SPACING_10B};
  ${mediaQuery.uptoMobile} {
    gap: ${Spacings.SPACING_4B};
    margin-top: ${Spacings.SPACING_6B};
  }
`;
const StyledButton = styled(Button)`
  padding: ${Spacings.SPACING_4B} ${Spacings.SPACING_6B};
  gap: ${Spacings.SPACING_1B};
  min-width: 188px;
  height: 48px;
  width: max-content;
`;
const ShareButton = styled(StyledButton)`
  gap: ${Spacings.SPACING_0B};
  ${mediaQuery.uptoMobile} {
    min-width: 116px;
  }
`;

const Info = styled(Typography)``;

const EducatorInfo = ({ author = {}, topicGroupName }) => {
  const authorFullName = getAuthorName(author);
  const { levelInfo } = author;
  return (
    <EducatorInfoWrapper>
      <Link href="/[authorID]" as={`/@${author.username}`}>
        <EducatorAvatar
          src={author.avatar}
          levelInfo={{
            ...(levelInfo || {}),
            currentLevelId: levelInfo?.levelId
          }}
          size="S"
        />
      </Link>
      <InfoWrapper>
        <Link href="/[authorID]" as={`/@${author.username}`}>
          <Name variant="h6">{authorFullName}</Name>
        </Link>
        {topicGroupName && (
          <Info variant="p3" color="var(--color-text-secondary)">
            {author.followersCount && (
              <>{`${nFormatter(author.followersCount)} followers • `}</>
            )}
            {`${topicGroupName}`}
          </Info>
        )}
      </InfoWrapper>
    </EducatorInfoWrapper>
  );
};

const PreviewWrapper = styled.div`
  position: relative;
  width: 464px;
  height: 266px;
  cursor: pointer;
  ${mediaQuery.uptoMobile} {
    width: 328px;
    height: 194px;
    grid-row: 1/2;
  }
`;

const ImageWrapper = styled.div`
  width: 245px;
  height: 245px;
  position: absolute;
  bottom: ${Spacings.SPACING_0B};
  left: 50%;
  transform: translateX(-50%);
  ${mediaQuery.uptoMobile} {
    width: 190px;
    height: 174px;
  }
`;

const EduImage = styled(Image)`
  object-fit: contain;
`;

const ReedMoreCTA = styled.div`
  position: relative;
  display: inline-block;
  text-transform: uppercase;
  font-weight: ${Fonts.HEAVY};
  color: var(--color-text-primary);
  margin-left: ${Spacings.SPACING_1B};
`;

const PlayIconButton = styled(Button)`
  position: absolute;
  bottom: ${Spacings.SPACING_4B};
  left: ${Spacings.SPACING_4B};
  height: 36px;
  background: var(--color-base-1);
  border-radius: ${Spacings.SPACING_22};
  text-transform: uppercase;
  padding: ${Spacings.SPACING_6} ${Spacings.SPACING_3B} ${Spacings.SPACING_6}
    ${Spacings.SPACING_6};
  font-size: ${Fonts.ULTRA_SMALL};
  letter-spacing: 0.02em;
  border: none;
  line-height: ${Spacings.SPACING_3B};
`;

const BackGroundImage = styled(EducatorThumbnailBackground)`
  width: 464px;
  height: 266px;
  ${mediaQuery.uptoMobile} {
    width: 328px;
    height: 200px;
  }
`;

const Preview = ({ url, color, handlePlayCTA, label }) => {
  return (
    <PreviewWrapper onClick={handlePlayCTA}>
      <BackGroundImage variant="content-thumbnail" color={color} />
      <ImageWrapper>
        <EduImage src={url} layout="fill" alt="edu-image" />
      </ImageWrapper>
      <PlayIconButton
        size="large"
        variant="secondary"
        label={label}
        startIcon={<PlayContained color="primary" width="24" height="24" />}
      />
    </PreviewWrapper>
  );
};

const Header = ({ playlistUID, handlePlayCTA, slug }) => {
  const { lessonDetails = {}, learnerMeta } = useLessonDetails(playlistUID);
  const author = lessonDetails?.authors?.[0] || {};
  const { color } = getTopicGroupAssetObj(lessonDetails?.topicGroupName);
  const [readMoreCTA, setReadMoreCTA] = useState(false);
  const [description, setDescription] = useState('');
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);

  const onShareClick = (e) => {
    e.stopPropagation();
    setIsShareDialogOpen(true);
  };

  const isMobile = useIsMobile();
  const handleSeeMoreClick = () => {
    setDescription(lessonDetails?.description);
    setReadMoreCTA(false);
  };

  useEffect(() => {
    const descriptionLength = (lessonDetails?.description || '').length;
    if (isMobile && descriptionLength > 110) {
      setDescription(
        `${lessonDetails?.description?.substr(0, 110)?.trim()}...`
      );
      setReadMoreCTA(true);
    } else {
      setDescription(lessonDetails?.description);
    }
  }, [lessonDetails?.description, isMobile]);

  const shareData = {
    permalink: `https://unacademy.com/curated-classes/${slug}/${playlistUID}`
  };

  const handlePreviewClick = () => {
    handlePlayCTA({
      last_primary_source_section: PREVIEW_THUMBNAIL,
      CTA_name: learnerMeta?.ctaHeader
    });
    segmentStorage.setItem('lpss', PREVIEW_THUMBNAIL);
  };

  const handleStartWatchingClick = () => {
    handlePlayCTA({
      last_primary_source_section: MAIN_CTA,
      CTA_name: learnerMeta?.ctaHeader
    });
  };

  return (
    <HeaderContainer>
      <PlayListInfo>
        <MetaInfo>
          <LanguageTag variant="default" size="small">
            {LANGUAGE_MAP[lessonDetails?.language]?.languageText || 'EN'}
          </LanguageTag>
          <Subject color={color} variant="h4">
            {lessonDetails?.topicGroupName}
          </Subject>
        </MetaInfo>
        <Title as="h1" variant="h2">
          {lessonDetails.name}
        </Title>
        <EducatorInfo
          author={author}
          topicGroupName={lessonDetails?.topicGroupName}
        />
        <Description variant="p2" color="var(--color-text-secondary)">
          {description}
          {readMoreCTA && (
            <ReedMoreCTA onClick={handleSeeMoreClick}>Read more</ReedMoreCTA>
          )}
        </Description>
        <ButtonWrapper>
          <StyledButton
            label={learnerMeta?.ctaHeader || 'Start watching'}
            onClick={handleStartWatchingClick}
            size="large"
            variant="primary"
            startIcon={<Play color="primary" width="12" height="12" />}
          />
          <ShareButton
            label="Share"
            onClick={onShareClick}
            size="large"
            variant="secondary"
            startIcon={<Forward color="primary" width="28" height="28" />}
          />
        </ButtonWrapper>
      </PlayListInfo>
      <Preview
        url={author.avatarV2 || author.oldAvatar}
        color={color}
        handlePlayCTA={handlePreviewClick}
        label={learnerMeta?.ctaHeader || 'Start watching'}
      />
      <Dialog
        onClose={() => setIsShareDialogOpen(false)}
        dialogState={isShareDialogOpen}
      >
        {isShareDialogOpen && (
          <Suspense fallback={null}>
            <ShareDialog data={shareData} useDataLink />
          </Suspense>
        )}
      </Dialog>
    </HeaderContainer>
  );
};

export default Header;
